import { useReducer, useCallback, useMemo } from "react";
import type {
  Puzzle, GameState, PlacedEmitter, InventoryEmitter, Pos, ClueStatus,
} from "../types.ts";
import { posKey } from "../types.ts";
import { getCell } from "../engine/grid.ts";
import { computeSignalMap, evaluateClues, checkWin, distinctRotations } from "../engine/signal.ts";
import { calculateStars } from "../engine/scoring.ts";

type Action =
  | { type: "SELECT_INVENTORY"; emitterId: string }
  | { type: "PLACE"; pos: Pos }
  | { type: "ROTATE"; pos: Pos }
  | { type: "PICKUP"; pos: Pos }
  | { type: "SUBMIT" }
  | { type: "UNDO" }
  | { type: "RESET" };

interface Snapshot {
  placedEmitters: PlacedEmitter[];
  inventory: InventoryEmitter[];
  heldEmitterId: string | null;
  moves: number;
}

let snapshots: Snapshot[] = [];

function initState(puzzle: Puzzle): GameState {
  snapshots = [];
  return {
    puzzle,
    board: puzzle.board,
    placedEmitters: [...puzzle.fixedEmitters],
    inventory: [...puzzle.inventory],
    heldEmitterId: null,
    phase: "placing",
    moves: 0,
    undoCount: 0,
    startedAt: Date.now(),
    completedAt: null,
    stars: 0,
    submitted: false,
  };
}

function saveSnapshot(state: GameState): void {
  snapshots.push({
    placedEmitters: state.placedEmitters.map((e) => ({ ...e })),
    inventory: state.inventory.map((e) => ({ ...e })),
    heldEmitterId: state.heldEmitterId,
    moves: state.moves,
  });
}

function reducer(state: GameState, action: Action): GameState {
  if (state.phase === "won" && action.type !== "RESET") return state;

  switch (action.type) {
    case "SELECT_INVENTORY": {
      const emitter = state.inventory.find((e) => e.id === action.emitterId);
      if (!emitter) return state;

      // Toggle selection
      if (state.heldEmitterId === action.emitterId) {
        return { ...state, heldEmitterId: null };
      }
      return { ...state, heldEmitterId: action.emitterId };
    }

    case "PLACE": {
      const { pos } = action;
      const cell = getCell(state.board, pos);
      if (!cell || cell.kind !== "empty") return state;

      // Check if there's already a placed emitter here
      const existingIdx = state.placedEmitters.findIndex(
        (e) => posKey(e.pos) === posKey(pos) && !e.fixed
      );

      if (state.heldEmitterId) {
        // We're holding an inventory emitter
        const invIdx = state.inventory.findIndex((e) => e.id === state.heldEmitterId);
        if (invIdx === -1) return state;
        const invEmitter = state.inventory[invIdx]!;

        saveSnapshot(state);

        if (existingIdx >= 0) {
          // Swap: pick up existing, place held
          const existing = state.placedEmitters[existingIdx]!;
          const newPlaced = [...state.placedEmitters];
          newPlaced[existingIdx] = {
            id: invEmitter.id,
            type: invEmitter.type,
            pos,
            rotation: invEmitter.rotation,
            fixed: false,
          };

          const newInventory = [...state.inventory];
          newInventory[invIdx] = {
            id: existing.id,
            type: existing.type,
            rotation: existing.rotation,
          };

          return checkForWin({
            ...state,
            placedEmitters: newPlaced,
            inventory: newInventory,
            heldEmitterId: null,
            moves: state.moves + 1,
          });
        }

        // Place on empty cell
        const newPlaced = [
          ...state.placedEmitters,
          {
            id: invEmitter.id,
            type: invEmitter.type,
            pos,
            rotation: invEmitter.rotation,
            fixed: false,
          },
        ];

        const newInventory = state.inventory.filter((e) => e.id !== state.heldEmitterId);

        return checkForWin({
          ...state,
          placedEmitters: newPlaced,
          inventory: newInventory,
          heldEmitterId: null,
          moves: state.moves + 1,
        });
      }

      // Not holding anything — if clicking an existing non-fixed emitter, rotate it
      if (existingIdx >= 0) {
        saveSnapshot(state);
        const existing = state.placedEmitters[existingIdx]!;
        const maxRot = distinctRotations(existing.type.shape);
        const newPlaced = [...state.placedEmitters];
        newPlaced[existingIdx] = {
          ...existing,
          rotation: (existing.rotation + 1) % maxRot,
        };
        return checkForWin({
          ...state,
          placedEmitters: newPlaced,
          moves: state.moves + 1,
        });
      }

      return state;
    }

    case "ROTATE": {
      const { pos } = action;
      const idx = state.placedEmitters.findIndex(
        (e) => posKey(e.pos) === posKey(pos) && !e.fixed
      );
      if (idx === -1) return state;

      saveSnapshot(state);
      const emitter = state.placedEmitters[idx]!;
      const maxRot = distinctRotations(emitter.type.shape);
      const newPlaced = [...state.placedEmitters];
      newPlaced[idx] = {
        ...emitter,
        rotation: (emitter.rotation + 1) % maxRot,
      };
      return checkForWin({
        ...state,
        placedEmitters: newPlaced,
        moves: state.moves + 1,
      });
    }

    case "PICKUP": {
      const { pos } = action;
      const idx = state.placedEmitters.findIndex(
        (e) => posKey(e.pos) === posKey(pos) && !e.fixed
      );
      if (idx === -1) return state;

      saveSnapshot(state);
      const emitter = state.placedEmitters[idx]!;
      const newPlaced = state.placedEmitters.filter((_, i) => i !== idx);
      const returnToInv: InventoryEmitter = {
        id: emitter.id,
        type: emitter.type,
        rotation: emitter.rotation,
      };

      return {
        ...state,
        placedEmitters: newPlaced,
        inventory: [...state.inventory, returnToInv],
        heldEmitterId: null,
        moves: state.moves + 1,
      };
    }

    case "SUBMIT": {
      const won = checkWin(state.board, state.placedEmitters, state.inventory.length === 0);
      if (won) {
        const emitterCount = state.puzzle.inventory.length;
        const stars = calculateStars(state.moves, emitterCount, state.undoCount);
        return {
          ...state,
          phase: "won",
          completedAt: Date.now(),
          stars,
          submitted: true,
        };
      }
      return { ...state, submitted: true };
    }

    case "UNDO": {
      const snap = snapshots.pop();
      if (!snap) return state;
      return {
        ...state,
        placedEmitters: snap.placedEmitters,
        inventory: snap.inventory,
        heldEmitterId: snap.heldEmitterId,
        moves: snap.moves,
        undoCount: state.undoCount + 1,
        submitted: false,
      };
    }

    case "RESET": {
      return initState(state.puzzle);
    }
  }
}

function checkForWin(state: GameState): GameState {
  // In blind mode, don't auto-check
  if (state.puzzle.difficulty.blind) return state;

  const won = checkWin(state.board, state.placedEmitters, state.inventory.length === 0);
  if (won) {
    const emitterCount = state.puzzle.inventory.length;
    const stars = calculateStars(state.moves, emitterCount, state.undoCount);
    return {
      ...state,
      phase: "won",
      completedAt: Date.now(),
      stars,
    };
  }
  return state;
}

export function useGame(puzzle: Puzzle) {
  const [state, dispatch] = useReducer(reducer, puzzle, initState);

  const selectInventory = useCallback(
    (emitterId: string) => dispatch({ type: "SELECT_INVENTORY", emitterId }),
    []
  );
  const place = useCallback(
    (pos: Pos) => dispatch({ type: "PLACE", pos }),
    []
  );
  const rotate = useCallback(
    (pos: Pos) => dispatch({ type: "ROTATE", pos }),
    []
  );
  const pickup = useCallback(
    (pos: Pos) => dispatch({ type: "PICKUP", pos }),
    []
  );
  const submit = useCallback(() => dispatch({ type: "SUBMIT" }), []);
  const undo = useCallback(() => dispatch({ type: "UNDO" }), []);
  const reset = useCallback(() => dispatch({ type: "RESET" }), []);

  // Compute clue statuses for rendering
  const clueStatuses = useMemo(() => {
    // In blind mode, only show after submit
    if (state.puzzle.difficulty.blind && !state.submitted) return null;
    const signalMap = computeSignalMap(state.board, state.placedEmitters);
    return evaluateClues(state.board, signalMap);
  }, [state.board, state.placedEmitters, state.submitted, state.puzzle.difficulty.blind]);

  return {
    state,
    clueStatuses,
    selectInventory,
    place,
    rotate,
    pickup,
    submit,
    undo,
    reset,
  };
}
